import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";

const KEY = "wua.journey.celebrated";
const read = () => { try { return new Set(JSON.parse(localStorage.getItem(KEY) || "[]")); } catch { return new Set(); } };
const write = (set) => { try { localStorage.setItem(KEY, JSON.stringify([...set])); } catch { /* private mode: celebrate anyway */ } };
const markSeen = (k) => { const seen = read(); seen.add(k); write(seen); };

// Stages to celebrate now: completed, and not yet celebrated in this browser. Run once per page, not per card.
export function useStageCelebrations(doneKeys, loaded) {
  const [playing, setPlaying] = useState(() => new Set());
  const sig = doneKeys.join(",");
  useEffect(() => {
    if (!loaded) return;
    // Forget stages that are no longer complete (reset), so finishing them again celebrates again.
    const seen = new Set([...read()].filter((k) => doneKeys.includes(k)));
    write(seen);
    // A stage is only marked seen once its celebration actually plays (card in view).
    const fresh = doneKeys.filter((k) => !seen.has(k));
    if (!fresh.length) return;
    setPlaying(new Set(fresh));
  }, [sig, loaded]); // eslint-disable-line react-hooks/exhaustive-deps
  return playing;
}

const DOTS = Array.from({ length: 10 }, (_, i) => (i / 10) * Math.PI * 2);

// Ring pulse, a burst of dots from the badge, and a "Stage complete" pill — played once the card scrolls into view.
export function StageCelebration({ pending, colour, label, stageKey }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [play, setPlay] = useState(false);
  useEffect(() => {
    if (!pending || !inView) return;
    setPlay(true);
    markSeen(stageKey);
    const t = setTimeout(() => setPlay(false), 2600);
    return () => clearTimeout(t);
  }, [pending, inView]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
    <span ref={ref} className="pointer-events-none absolute inset-0" aria-hidden />
    <AnimatePresence>
      {play && (
        <motion.div className="pointer-events-none absolute inset-0 z-10" aria-hidden data-testid={`journey-map-celebration-${stageKey}`}
          initial={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.4 } }}>
          {!reduce && (
            <>
              <motion.span className="absolute inset-0 rounded-xl border-2" style={{ borderColor: colour }}
                initial={{ scale: 1, opacity: 0.8 }} animate={{ scale: 1.06, opacity: 0 }} transition={{ duration: 0.9, ease: "easeOut" }} />
              {DOTS.map((a, i) => (
                <motion.span key={i} className="absolute h-1.5 w-1.5 rounded-full"
                  style={{ left: 36, top: 36, backgroundColor: i % 2 ? colour : "#10B981" }}
                  initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                  animate={{ x: Math.cos(a) * 46, y: Math.sin(a) * 46, opacity: 0, scale: 0.4 }}
                  transition={{ duration: 0.8, ease: "easeOut", delay: 0.1 }} />
              ))}
            </>
          )}
          <span className="absolute inset-x-0 -top-3 flex justify-center">
            <motion.span className="rounded-full bg-emerald-500 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-white shadow-md"
              initial={{ opacity: 0, y: reduce ? 0 : 6, scale: reduce ? 1 : 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.35, ease: "easeOut", delay: 0.15 }}>
              {label} complete
            </motion.span>
          </span>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
}
